<template>
  <Sheet>
    <template #leading>
      <Sheet.Button :icon="CloseIcon" @click="close()" />
    </template>
    <template #title>Copyright settings</template>
    <template #trailing>
      <Sheet.Button
        variant="primary"
        :icon="CheckmarkIcon"
        :disabled="!canEditDetails"
        @click="save"
      />
    </template>

    <Sheet.Body class="mt-5">
      <template v-if="!isOwner">
        <Alert
          v-if="isCoOwner"
          variant="warning"
          :icon="CoOwnerIcon"
          class="mb-[14px]"
        >
          You are a co-owner of this file. You can only view its settings and
          remove yourself as a co-owner.
        </Alert>

        <Alert
          v-else-if="isAuthor"
          variant="warning"
          :icon="AuthorIcon"
          class="mb-[14px]"
        >
          You are an author of this file. You can only view its settings and
          remove yourself as an author.
        </Alert>
      </template>

      <div class="mb-[14px]">
        <Input
          v-model="title"
          size="xl"
          label="File title"
          :disabled="!canEditDetails"
          :error="titleError"
        />
      </div>
      <div ref="descriptionField" class="mb-[14px]">
        <Textarea
          v-model="description"
          size="xl"
          label="Description"
          :disabled="!canEditDetails"
        />
      </div>

      <div
        class="w-full py-[15px] border-b border-gray-800/20 flex items-center justify-between"
      >
        <div>
          <div class="font-medium text-[14px] mb-1">Ownership</div>
          <div class="flex items-center">
            <div class="flex items-center -ml-0.5 mr-2.5">
              <div
                v-for="(owner, index) in owners.slice(0, 3)"
                :key="owner.id"
                class="size-[30px] border-2 border-white flex items-center justify-center rounded-full"
                :class="[
                  !index && 'z-1',
                  index === 1 && 'z-0',
                  index && '-ml-[14px]',
                ]"
              >
                <Avatar :src="owner.avatarUrl" :size="26" />
              </div>
            </div>
            <Link icon="arrow" @click="handleOwnersClick">
              {{ ownersLinkCopy }}
            </Link>
          </div>
        </div>
        <Button
          variant="stroke"
          size="lg"
          :disabled="shouldDisable"
          @click="handleOwnersClick"
        >
          {{ manageActionCopy }}
        </Button>
      </div>

      <div
        class="w-full py-[15px] border-b border-gray-800/20 flex items-center justify-between"
      >
        <div>
          <div class="font-medium text-[14px] mb-1">Authors</div>
          <div class="flex items-center">
            <div v-if="authorsCount" class="flex items-center -ml-0.5 mr-2.5">
              <div
                v-for="(author, index) in authors.slice(0, 3)"
                :key="author.id"
                class="size-[30px] border-2 border-white flex items-center justify-center rounded-full"
                :class="[
                  !index && 'z-1',
                  index === 1 && 'z-0',
                  index && '-ml-[14px]',
                ]"
              >
                <Avatar :src="author.avatarUrl" :size="26" />
              </div>
            </div>
            <div v-if="!authorsCount">0 authors</div>
            <Link v-else icon="arrow" @click="handleAuthorsClick">
              {{ authorsLinkCopy }}
            </Link>
          </div>
        </div>
        <Button
          size="lg"
          variant="stroke"
          :disabled="shouldDisable"
          @click="handleAuthorsClick"
        >
          {{ manageActionCopy }}
        </Button>
      </div>

      <div
        class="w-full py-[15px] border-b border-gray-800/20 flex items-center justify-between"
      >
        <div>
          <div class="font-medium text-[14px] mb-1">Invitees</div>
          <div class="flex items-center">
            <div v-if="!viewersCount">0 have access</div>
            <Link v-else icon="arrow" @click="handleInvitesClick">
              {{ invitesLinkCopy }}
            </Link>
          </div>
        </div>
        <Button
          size="lg"
          variant="stroke"
          :disabled="shouldDisable"
          @click="handleInvitesClick"
        >
          Manage
        </Button>
      </div>
      <div
        class="w-full py-[15px] border-b border-gray-800/20 flex items-center justify-between"
      >
        <div>
          <div class="font-medium text-[14px] mb-1">Privacy controls</div>
          <div>File is {{ privateStatus }}</div>
        </div>
        <Button
          v-if="isOwner"
          size="lg"
          variant="stroke"
          :disabled="shouldDisable"
          @click="handlePrivacyClick"
        >
          Manage
        </Button>
      </div>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import {
  Sheet,
  Alert,
  Button,
  Input,
  Textarea,
  Avatar,
  Link,
  useSheet,
} from '@dacr/library'
import { CloseIcon, CheckmarkIcon, CoOwnerIcon, AuthorIcon } from '@dacr/icons'
import type { File } from '@/shared/graphql/router/graphql'
import { useFileSettings, useDescriptionFocus } from './useFileSettings'

const { close } = useSheet()

const props = defineProps<{
  file: File
  focusDescription?: boolean
}>()

const descriptionField = useDescriptionFocus(() => props.focusDescription)

const {
  title,
  description,
  titleError,
  isOwner,
  isCoOwner,
  isAuthor,
  shouldDisable,
  canEditDetails,
  manageActionCopy,
  privateStatus,
  owners,
  authors,
  authorsCount,
  viewersCount,
  ownersLinkCopy,
  authorsLinkCopy,
  invitesLinkCopy,
  handleOwnersClick,
  handleAuthorsClick,
  handleInvitesClick,
  handlePrivacyClick,
  save,
} = useFileSettings(props.file, close)
</script>
